/**
 * `studio/analytics.html` §2 — revenue by billing model.
 *
 * The proportion bar is the current month split four ways; the columns beside it are
 * six months of totals, with the by-model breakdown in the table view. Only one of
 * the two carries a narration line, because CONTRACT.md §2.13 allows a chart either a
 * caption or a commentary, and DESIGN.md's own "what done looks like" names this page:
 * "every chart currently carries both a caption *and* a footer commentary. One or the
 * other, never both."
 *
 * The share is `share`, a fraction on `RevenueShareSchema`, printed by `render.ts` —
 * `format.percent` takes basis points and would throw on `0.6777234054384697`.
 */

import {
  Chart,
  ColumnChart,
  COLUMN_VIEW_BOX,
  Legend,
  LegendKey,
  ProportionBar,
  PROPORTION_VIEW_BOX,
} from '../../../components/portal/Chart.js'
import { Section, SectionHead } from '../../../components/portal/Layout.js'
import { Region, RegionCols } from '../../../components/portal/Region.js'
import { useFormat, type Formatter } from '../../../format/useFormat.js'
import { currencyOf, moneyTicks } from './axis.js'
import type { AnalyticsReads, RevenueData } from './data.js'
import { MODEL_LABEL, MODEL_SLOT } from './labels.js'
import { Panel } from './Panel.js'
import { shortMonth, WHOLE_AMOUNT } from './render.js'
import { share } from '../../../format/percent.js'
import { onScreen, revenueMonthsTable, revenueShareTable } from './tables.js'

export function RevenueSection({ query }: { readonly query: AnalyticsReads['revenue'] }) {
  return (
    <Section>
      <SectionHead title="Revenue by billing model" note="Recognised, all models" pushNote />
      <Region>
        <Panel query={query} subject="revenue by billing model">
          {(revenue) => (
            <RegionCols>
              <MixChart revenue={revenue} />
              <MonthsChart revenue={revenue} />
            </RegionCols>
          )}
        </Panel>
      </Region>
    </Section>
  )
}

/**
 * Which month the mix is of.
 *
 * The read takes no period (`data.ts`), so it names the month it actually answered
 * for rather than the one the seg is on.
 */
export function revenueHeading(revenue: RevenueData, format: Formatter): string {
  const month = revenue.months.at(-1)?.month
  return month === undefined ? 'This month' : format.monthLong(month)
}

/* ------------------------------------------------------ the current month's mix */

function MixChart({ revenue }: { readonly revenue: RevenueData }) {
  const format = useFormat()
  const heading = revenueHeading(revenue, format)
  const shares = revenue.current.shares

  return (
    <Chart
      conclusion={
        shares.length === 0
          ? `${heading} recognised no revenue at all.`
          : `${heading} revenue of ${format.money(revenue.current.total, WHOLE_AMOUNT)} by billing model: ${shares
              .map(
                (one) =>
                  `${MODEL_LABEL[one.model]} ${format.money(one.amount, WHOLE_AMOUNT)}, ${share(format, one.share)}`,
              )
              .join('; ')}.`
      }
      viewBox={PROPORTION_VIEW_BOX}
      legend={
        <Legend>
          {shares.map((one) => (
            <LegendKey key={one.model} slot={MODEL_SLOT[one.model]}>
              {MODEL_LABEL[one.model]}
            </LegendKey>
          ))}
        </Legend>
      }
      note={smallestNote(revenue, format)}
      table={onScreen(revenueShareTable(revenue, heading, format))}
    >
      <ProportionBar
        startLabel={format.money(
          { amount: 0, currency: revenue.current.total.currency },
          WHOLE_AMOUNT,
        )}
        endLabel={format.money(revenue.current.total, WHOLE_AMOUNT)}
        parts={shares.map((one, index) => ({
          label: MODEL_LABEL[one.model],
          value: one.amount.amount,
          slot: MODEL_SLOT[one.model],
          title: `${MODEL_LABEL[one.model]}: ${format.money(one.amount, WHOLE_AMOUNT)} — ${share(format, one.share)}`,
          inside:
            index === 0
              ? `${MODEL_LABEL[one.model]} · ${share(format, one.share)}`
              : share(format, one.share),
        }))}
      />
    </Chart>
  )
}

/* ------------------------------------------------------------ six months of it */

function MonthsChart({ revenue }: { readonly revenue: RevenueData }) {
  const format = useFormat()
  const months = revenue.months
  const currency = currencyOf(months.map((one) => one.total))
  const peak = Math.max(...months.map((one) => one.total.amount), 0)

  return (
    <Chart
      conclusion={
        months.length === 0
          ? 'No revenue has been recognised yet.'
          : `Total recognised revenue by month: ${months
              .map((one) => `${format.month(one.month)} ${format.money(one.total, WHOLE_AMOUNT)}`)
              .join('; ')}.`
      }
      viewBox={COLUMN_VIEW_BOX}
      note={lowestNote(months, format)}
      table={onScreen(revenueMonthsTable(revenue, format))}
    >
      <ColumnChart
        data={months.map((one) => ({
          label: shortMonth(one.month, format),
          value: one.total.amount,
          title: `${format.monthLong(one.month)}: ${format.money(one.total, WHOLE_AMOUNT)} — retainer ${format.money(one.retainer, WHOLE_AMOUNT)}, project ${format.money(one.project, WHOLE_AMOUNT)}, extra ${format.money(one.extra, WHOLE_AMOUNT)}, usage ${format.money(one.usage, WHOLE_AMOUNT)}`,
        }))}
        ticks={moneyTicks(peak, currency, format)}
        slots={['c1']}
      />
    </Chart>
  )
}

/**
 * The mix bar's narration line: the sliver on the right, named.
 *
 * `analytics.html` says "Usage is the sliver on the right — $218, 1.5%. It is a
 * rounding error today, and it is the only line here that scales without our time."
 * The second sentence is the studio's own editorial and belongs behind §3's policy
 * fold (DESIGN.md P7); the first is two fields of the smallest share, which is also
 * the segment `ProportionBar` is too narrow to label in place.
 */
function smallestNote(revenue: RevenueData, format: Formatter): string {
  const shares = revenue.current.shares
  const smallest = shares.reduce<RevenueShare | null>(
    (found, one) => (found === null || one.share < found.share ? one : found),
    null,
  )
  if (smallest === null) return 'Nothing was recognised this month, so there is nothing to split.'
  return `${MODEL_LABEL[smallest.model]} is the smallest line of the month at ${format.money(smallest.amount, WHOLE_AMOUNT)}, ${share(format, smallest.share)} — too narrow for the bar to label.`
}

type RevenueShare = RevenueData['current']['shares'][number]
type RevenueMonth = RevenueData['months'][number]

/**
 * The narration line: the quietest month, and how much of it was project work.
 *
 * `analytics.html` says "June dipped because no project milestone landed" — a cause
 * no payload carries. What the payload does carry is which month was lowest and what
 * its project column was, which is the same observation without the claim.
 */
function lowestNote(months: readonly RevenueMonth[], format: Formatter): string {
  const lowest = months.reduce<RevenueMonth | null>(
    (found, one) => (found === null || one.total.amount < found.total.amount ? one : found),
    null,
  )
  if (lowest === null) return 'Nothing has been invoiced in these months.'
  return `${format.monthLong(lowest.month)} is the quietest of these months at ${format.money(lowest.total, WHOLE_AMOUNT)}, of which ${format.money(lowest.project, WHOLE_AMOUNT)} was project work.`
}
